'use client';
import React, { useState } from 'react';
import {
  Bell, Mail, Monitor, Check, Save, RefreshCw, ChevronDown,
  AlertTriangle, DollarSign, Shield, FileText, TrendingUp,
  ArrowLeftRight, CheckCircle, Info, Zap, Clock, ToggleLeft, ToggleRight
} from 'lucide-react';
import SectionHeader from '@/components/ui/SectionHeader';


interface EventType {
  id: string;
  label: string;
  description: string;
  category: string;
  defaultEmail: boolean;
  defaultInApp: boolean;
  severity: 'critical' | 'warning' | 'info';
}

const eventCategories = [
  {
    id: 'investment',
    label: 'Investment Activity',
    icon: TrendingUp,
    color: 'text-accent',
    bg: 'bg-accent/10',
  },
  {
    id: 'settlement',
    label: 'Settlement & Payments',
    icon: ArrowLeftRight,
    color: 'text-positive',
    bg: 'bg-positive/10',
  },
  {
    id: 'compliance',
    label: 'Compliance & KYC',
    icon: Shield,
    color: 'text-warning',
    bg: 'bg-warning/10',
  },
  {
    id: 'distribution',
    label: 'Distributions',
    icon: DollarSign,
    color: 'text-info',
    bg: 'bg-info/10',
  },
  {
    id: 'documents',
    label: 'Documents & Reporting',
    icon: FileText,
    color: 'text-muted-foreground',
    bg: 'bg-surface-3',
  },
  {
    id: 'alerts',
    label: 'Risk & Alerts',
    icon: AlertTriangle,
    color: 'text-critical',
    bg: 'bg-critical/10',
  },
];

const eventTypes: EventType[] = [
  // Investment
  { id: 'inv-committed', label: 'Investment Committed', description: 'When your investment commitment is confirmed', category: 'investment', defaultEmail: true, defaultInApp: true, severity: 'info' },
  { id: 'inv-campaign-close', label: 'Campaign Closes', description: 'When a campaign you invested in reaches its close date', category: 'investment', defaultEmail: true, defaultInApp: true, severity: 'info' },
  { id: 'inv-campaign-update', label: 'Campaign Milestone Update', description: 'Issuer posts a progress update or milestone', category: 'investment', defaultEmail: false, defaultInApp: true, severity: 'info' },
  { id: 'inv-new-opportunity', label: 'New Investment Opportunity', description: 'New campaigns matching your investment profile', category: 'investment', defaultEmail: false, defaultInApp: false, severity: 'info' },
  // Settlement
  { id: 'stl-initiated', label: 'Settlement Initiated', description: 'A settlement batch for your investment has been initiated', category: 'settlement', defaultEmail: true, defaultInApp: true, severity: 'info' },
  { id: 'stl-completed', label: 'Settlement Completed', description: 'Funds have been successfully settled and transferred', category: 'settlement', defaultEmail: true, defaultInApp: true, severity: 'info' },
  { id: 'stl-failed', label: 'Settlement Failed', description: 'A settlement attempt has failed and requires attention', category: 'settlement', defaultEmail: true, defaultInApp: true, severity: 'critical' },
  { id: 'stl-discrepancy', label: 'Reconciliation Discrepancy', description: 'A discrepancy has been detected in your settlement batch', category: 'settlement', defaultEmail: true, defaultInApp: true, severity: 'critical' },
  // Compliance
  { id: 'kyc-approved', label: 'KYC Approved', description: 'Your KYC verification has been approved', category: 'compliance', defaultEmail: true, defaultInApp: true, severity: 'info' },
  { id: 'kyc-rejected', label: 'KYC Rejected / Action Required', description: 'Your KYC requires additional documents or action', category: 'compliance', defaultEmail: true, defaultInApp: true, severity: 'critical' },
  { id: 'kyc-expiring', label: 'KYC Expiring Soon', description: 'Your KYC verification expires within 30 days', category: 'compliance', defaultEmail: true, defaultInApp: true, severity: 'warning' },
  { id: 'compliance-hold', label: 'Compliance Hold Placed', description: 'A compliance hold has been placed on your account', category: 'compliance', defaultEmail: true, defaultInApp: true, severity: 'critical' },
  // Distribution
  { id: 'dist-scheduled', label: 'Distribution Scheduled', description: 'A distribution has been scheduled for your investment', category: 'distribution', defaultEmail: true, defaultInApp: true, severity: 'info' },
  { id: 'dist-paid', label: 'Distribution Paid', description: 'Distribution funds have been transferred to your account', category: 'distribution', defaultEmail: true, defaultInApp: true, severity: 'info' },
  { id: 'dist-delayed', label: 'Distribution Delayed', description: 'A scheduled distribution has been delayed', category: 'distribution', defaultEmail: true, defaultInApp: true, severity: 'warning' },
  // Documents
  { id: 'doc-new', label: 'New Document Available', description: 'A new document has been added to your Document Vault', category: 'documents', defaultEmail: false, defaultInApp: true, severity: 'info' },
  { id: 'doc-signature-required', label: 'Signature Required', description: 'A document requires your electronic signature', category: 'documents', defaultEmail: true, defaultInApp: true, severity: 'warning' },
  { id: 'doc-report-ready', label: 'Periodic Report Ready', description: 'Your quarterly or annual investor report is available', category: 'documents', defaultEmail: true, defaultInApp: false, severity: 'info' },
  // Alerts
  { id: 'risk-flag', label: 'Account Risk Flag', description: 'A risk flag has been raised on your account', category: 'alerts', defaultEmail: true, defaultInApp: true, severity: 'critical' },
  { id: 'risk-campaign', label: 'Campaign Risk Update', description: 'Risk rating change on a campaign you are invested in', category: 'alerts', defaultEmail: true, defaultInApp: true, severity: 'warning' },
  { id: 'security-login', label: 'New Login Detected', description: 'A new device or location has logged into your account', category: 'alerts', defaultEmail: true, defaultInApp: true, severity: 'warning' },
];

const frequencyOptions = [
  { value: 'immediate', label: 'Immediate', description: 'As soon as the event occurs' },
  { value: 'hourly', label: 'Hourly Digest', description: 'Bundled every hour' },
  { value: 'daily', label: 'Daily Digest', description: 'Once per day at 08:00 UTC' },
  { value: 'weekly', label: 'Weekly Summary', description: 'Every Monday at 08:00 UTC' },
];

const severityBadge = {
  critical: <span className="inline-flex items-center gap-1 text-xs font-medium px-1.5 py-0.5 rounded bg-critical/10 text-critical">Critical</span>,
  warning: <span className="inline-flex items-center gap-1 text-xs font-medium px-1.5 py-0.5 rounded bg-warning/10 text-warning">Warning</span>,
  info: <span className="inline-flex items-center gap-1 text-xs font-medium px-1.5 py-0.5 rounded bg-info/10 text-info">Info</span>,
};

export default function NotificationPreferences() {
  const [emailEnabled, setEmailEnabled] = useState(true);
  const [inAppEnabled, setInAppEnabled] = useState(true);
  const [emailFrequency, setEmailFrequency] = useState('immediate');
  const [inAppFrequency, setInAppFrequency] = useState('immediate');
  const [emailAddress, setEmailAddress] = useState('investor@example.com');
  const [saved, setSaved] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState<string | null>('investment');

  // Per-event channel preferences
  const [prefs, setPrefs] = useState<Record<string, { email: boolean; inApp: boolean }>>(() => {
    const init: Record<string, { email: boolean; inApp: boolean }> = {};
    eventTypes.forEach((e) => {
      init[e.id] = { email: e.defaultEmail, inApp: e.defaultInApp };
    });
    return init;
  });

  const togglePref = (eventId: string, channel: 'email' | 'inApp') => {
    setPrefs((prev) => ({
      ...prev,
      [eventId]: { ...prev[eventId], [channel]: !prev[eventId][channel] },
    }));
  };

  const handleSave = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const getCategoryStats = (catId: string) => {
    const events = eventTypes.filter((e) => e.category === catId);
    const emailOn = events.filter((e) => prefs[e.id]?.email).length;
    const inAppOn = events.filter((e) => prefs[e.id]?.inApp).length;
    return { total: events.length, emailOn, inAppOn };
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-foreground tracking-tight">Notification Preferences</h1>
          <p className="text-sm text-muted-foreground mt-1">Configure alert channels, delivery frequency, and event subscriptions</p>
        </div>
        <div className="flex items-center gap-2">
          <button className="btn-secondary text-xs gap-1.5" onClick={() => {
            const reset: Record<string, { email: boolean; inApp: boolean }> = {};
            eventTypes.forEach((e) => { reset[e.id] = { email: e.defaultEmail, inApp: e.defaultInApp }; });
            setPrefs(reset);
          }}>
            <RefreshCw size={12} /> Reset to Defaults
          </button>
          <button
            onClick={handleSave}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold transition-all ${
              saved
                ? 'bg-positive/15 text-positive border border-positive/20' :'btn-primary'
            }`}
          >
            {saved ? <><Check size={12} /> Saved!</> : <><Save size={12} /> Save Preferences</>}
          </button>
        </div>
      </div>

      {/* Channel Configuration */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Email Channel */}
        <div className={`glass-card rounded-xl p-5 border-l-4 transition-opacity ${emailEnabled ? 'border-l-accent' : 'border-l-border opacity-70'}`}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-accent/10 flex items-center justify-center">
                <Mail size={18} className="text-accent" />
              </div>
              <div>
                <p className="font-semibold text-foreground">Email Notifications</p>
                <p className="text-xs text-muted-foreground">Delivered to your registered email</p>
              </div>
            </div>
            <button
              onClick={() => setEmailEnabled(!emailEnabled)}
              className="flex-shrink-0"
              title={emailEnabled ? 'Disable email notifications' : 'Enable email notifications'}
            >
              {emailEnabled
                ? <ToggleRight size={28} className="text-accent" />
                : <ToggleLeft size={28} className="text-muted-foreground" />}
            </button>
          </div>

          {emailEnabled && (
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Email Address</label>
                <input
                  type="email"
                  value={emailAddress}
                  onChange={(e) => setEmailAddress(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-surface-3 border border-border text-sm text-foreground focus:outline-none focus:border-accent/50 font-mono-data"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Delivery Frequency</label>
                <div className="grid grid-cols-2 gap-2">
                  {frequencyOptions.map((opt) => (
                    <button
                      key={`email-freq-${opt.value}`}
                      onClick={() => setEmailFrequency(opt.value)}
                      className={`px-3 py-2 rounded-lg text-left border transition-colors ${
                        emailFrequency === opt.value
                          ? 'bg-accent/10 border-accent/30 text-accent' :'bg-surface-3 border-border text-muted-foreground hover:border-accent/20'
                      }`}
                    >
                      <p className="text-xs font-semibold">{opt.label}</p>
                      <p className="text-xs opacity-70 mt-0.5">{opt.description}</p>
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-2 p-2.5 rounded-lg bg-info/5 border border-info/15">
                <Info size={12} className="text-info flex-shrink-0" />
                <p className="text-xs text-muted-foreground">Critical alerts (KYC holds, settlement failures) are always sent immediately regardless of frequency setting.</p>
              </div>
            </div>
          )}
        </div>

        {/* In-App Channel */}
        <div className={`glass-card rounded-xl p-5 border-l-4 transition-opacity ${inAppEnabled ? 'border-l-positive' : 'border-l-border opacity-70'}`}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-positive/10 flex items-center justify-center">
                <Monitor size={18} className="text-positive" />
              </div>
              <div>
                <p className="font-semibold text-foreground">In-App Notifications</p>
                <p className="text-xs text-muted-foreground">Shown in the notification centre</p>
              </div>
            </div>
            <button
              onClick={() => setInAppEnabled(!inAppEnabled)}
              className="flex-shrink-0"
              title={inAppEnabled ? 'Disable in-app notifications' : 'Enable in-app notifications'}
            >
              {inAppEnabled
                ? <ToggleRight size={28} className="text-positive" />
                : <ToggleLeft size={28} className="text-muted-foreground" />}
            </button>
          </div>

          {inAppEnabled && (
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Notification Frequency</label>
                <div className="grid grid-cols-2 gap-2">
                  {frequencyOptions.slice(0, 3).map((opt) => (
                    <button
                      key={`inapp-freq-${opt.value}`}
                      onClick={() => setInAppFrequency(opt.value)}
                      className={`px-3 py-2 rounded-lg text-left border transition-colors ${
                        inAppFrequency === opt.value
                          ? 'bg-positive/10 border-positive/30 text-positive' :'bg-surface-3 border-border text-muted-foreground hover:border-positive/20'
                      }`}
                    >
                      <p className="text-xs font-semibold">{opt.label}</p>
                      <p className="text-xs opacity-70 mt-0.5">{opt.description}</p>
                    </button>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <p className="text-xs font-medium text-muted-foreground">Display Options</p>
                {[
                  { label: 'Show badge count on bell icon', defaultOn: true },
                  { label: 'Play sound for critical alerts', defaultOn: false },
                  { label: 'Show desktop push notifications', defaultOn: true },
                ].map((opt) => {
                  const [on, setOn] = useState(opt.defaultOn);
                  return (
                    <div key={opt.label} className="flex items-center justify-between py-1.5 px-3 rounded-lg bg-surface-3/60">
                      <span className="text-xs text-muted-foreground">{opt.label}</span>
                      <button onClick={() => setOn(!on)}>
                        {on
                          ? <ToggleRight size={20} className="text-positive" />
                          : <ToggleLeft size={20} className="text-muted-foreground" />}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Event Type Subscriptions */}
      <div className="glass-card rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
          <SectionHeader title="Event Subscriptions" subtitle="Choose which events trigger notifications per channel" />
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <span className="flex items-center gap-1.5"><Mail size={11} /> Email</span>
            <span className="flex items-center gap-1.5"><Monitor size={11} /> In-App</span>
          </div>
        </div>

        <div className="divide-y divide-border">
          {eventCategories.map((cat) => {
            const catEvents = eventTypes.filter((e) => e.category === cat.id);
            const stats = getCategoryStats(cat.id);
            const isExpanded = expandedCategory === cat.id;

            return (
              <div key={cat.id}>
                {/* Category header */}
                <button
                  className="w-full px-4 py-3 flex items-center gap-3 hover:bg-surface-3/30 transition-colors text-left"
                  onClick={() => setExpandedCategory(isExpanded ? null : cat.id)}
                >
                  <div className={`w-7 h-7 rounded-lg ${cat.bg} flex items-center justify-center flex-shrink-0`}>
                    <cat.icon size={14} className={cat.color} />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-foreground">{cat.label}</p>
                    <p className="text-xs text-muted-foreground">
                      {stats.emailOn}/{stats.total} email · {stats.inAppOn}/{stats.total} in-app enabled
                    </p>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    {/* Quick toggles for entire category */}
                    <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                      <button
                        title="Toggle all email for this category"
                        onClick={() => {
                          const allOn = stats.emailOn === stats.total;
                          setPrefs((prev) => {
                            const next = { ...prev };
                            catEvents.forEach((e) => { next[e.id] = { ...next[e.id], email: !allOn }; });
                            return next;
                          });
                        }}
                        className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium border transition-colors ${
                          stats.emailOn === stats.total
                            ? 'bg-accent/10 text-accent border-accent/20'
                            : stats.emailOn > 0
                            ? 'bg-surface-3 text-muted-foreground border-border'
                            : 'bg-surface-3 text-muted-foreground border-border'
                        }`}
                      >
                        <Mail size={10} />
                        {stats.emailOn === stats.total ? 'All' : `${stats.emailOn}`}
                      </button>
                      <button
                        title="Toggle all in-app for this category"
                        onClick={() => {
                          const allOn = stats.inAppOn === stats.total;
                          setPrefs((prev) => {
                            const next = { ...prev };
                            catEvents.forEach((e) => { next[e.id] = { ...next[e.id], inApp: !allOn }; });
                            return next;
                          });
                        }}
                        className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium border transition-colors ${
                          stats.inAppOn === stats.total
                            ? 'bg-positive/10 text-positive border-positive/20' :'bg-surface-3 text-muted-foreground border-border'
                        }`}
                      >
                        <Monitor size={10} />
                        {stats.inAppOn === stats.total ? 'All' : `${stats.inAppOn}`}
                      </button>
                    </div>
                    {isExpanded ? <ChevronDown size={14} className="text-muted-foreground" /> : <ChevronDown size={14} className="text-muted-foreground rotate-[-90deg]" />}
                  </div>
                </button>

                {/* Event rows */}
                {isExpanded && (
                  <div className="bg-surface-3/20 divide-y divide-border/50">
                    {catEvents.map((event) => {
                      const pref = prefs[event.id] || { email: false, inApp: false };
                      return (
                        <div key={event.id} className="px-4 py-3 flex items-center gap-4 hover:bg-surface-3/30 transition-colors">
                          <div className="flex-1 pl-10">
                            <div className="flex items-center gap-2 mb-0.5">
                              <p className="text-sm text-foreground font-medium">{event.label}</p>
                              {severityBadge[event.severity]}
                            </div>
                            <p className="text-xs text-muted-foreground">{event.description}</p>
                          </div>
                          <div className="flex items-center gap-4 flex-shrink-0">
                            {/* Email toggle */}
                            <div className="flex flex-col items-center gap-1">
                              <span className="text-xs text-muted-foreground hidden sm:block">Email</span>
                              <button
                                onClick={() => togglePref(event.id, 'email')}
                                disabled={!emailEnabled}
                                className={`w-8 h-8 rounded-lg flex items-center justify-center border transition-colors ${
                                  pref.email && emailEnabled
                                    ? 'bg-accent/15 border-accent/30 text-accent' :'bg-surface-3 border-border text-muted-foreground opacity-50'
                                }`}
                                title={pref.email ? 'Disable email for this event' : 'Enable email for this event'}
                              >
                                {pref.email && emailEnabled ? <Mail size={14} /> : <Mail size={14} className="opacity-40" />}
                              </button>
                            </div>
                            {/* In-App toggle */}
                            <div className="flex flex-col items-center gap-1">
                              <span className="text-xs text-muted-foreground hidden sm:block">In-App</span>
                              <button
                                onClick={() => togglePref(event.id, 'inApp')}
                                disabled={!inAppEnabled}
                                className={`w-8 h-8 rounded-lg flex items-center justify-center border transition-colors ${
                                  pref.inApp && inAppEnabled
                                    ? 'bg-positive/15 border-positive/30 text-positive' :'bg-surface-3 border-border text-muted-foreground opacity-50'
                                }`}
                                title={pref.inApp ? 'Disable in-app for this event' : 'Enable in-app for this event'}
                              >
                                {pref.inApp && inAppEnabled ? <Monitor size={14} /> : <Monitor size={14} className="opacity-40" />}
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Summary footer */}
      <div className="glass-card rounded-xl p-4 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-4 text-xs text-muted-foreground flex-wrap">
          <span className="flex items-center gap-1.5">
            <Zap size={12} className="text-accent" />
            <span>{Object.values(prefs).filter(p => p.email).length} email events active</span>
          </span>
          <span className="flex items-center gap-1.5">
            <Bell size={12} className="text-positive" />
            <span>{Object.values(prefs).filter(p => p.inApp).length} in-app events active</span>
          </span>
          <span className="flex items-center gap-1.5">
            <Clock size={12} />
            <span>Email: {frequencyOptions.find(f => f.value === emailFrequency)?.label} · In-App: {frequencyOptions.find(f => f.value === inAppFrequency)?.label}</span>
          </span>
        </div>
        <button
          onClick={handleSave}
          className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold transition-all ${
            saved
              ? 'bg-positive/15 text-positive border border-positive/20' :'btn-primary'
          }`}
        >
          {saved ? <><CheckCircle size={12} /> Preferences Saved</> : <><Save size={12} /> Save All Preferences</>}
        </button>
      </div>
    </div>
  );
}
